import {useEffect, useMemo, useState} from "react";
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ArrowDown, ArrowLeft, ArrowUp, GripVertical, RotateCcw, Save} from "lucide-react";
import {Link} from "react-router-dom";
import {mapApiError} from "../../shared/api/errors";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {DataState} from "../../shared/components/DataState";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {ToolbarSelect} from "../../shared/components/PageToolbar";
import {useTranslation} from "../../shared/i18n";
import {
    loadPlaceOrdering,
    loadPlaceProperties,
    loadPropertyCountries,
    savePlaceOrdering,
} from "./api";
import {useActiveAdminCountryScope} from "./useActiveAdminCountryScope";
import "./organizations.css";

const PLATFORM_ROLES = new Set([ADMIN_ROLES.SUPERADMIN, ADMIN_ROLES.ADMINISTRATOR]);
const MAX_BACKEND_MESSAGE_LENGTH = 500;

const membershipDriftMessage = (error) => {
    if (error?.response?.status !== 409) return null;
    const message = error.response.data?.message;
    return typeof message === "string" && message.trim()
        ? message.slice(0, MAX_BACKEND_MESSAGE_LENGTH)
        : null;
};

const moveRow = (rows, fromIndex, toIndex) => {
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= rows.length || toIndex >= rows.length) return rows;
    const next = [...rows];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
};

const orderingCityOptions = (properties) => (
    (properties?.cities || properties?.filterCities || [])
        .filter((city) => city != null && Number(city.id) !== 0)
);

const PositionRow = ({row, index, count, pending, onMove}) => {
    const {t} = useTranslation();
    const draggable = useDraggable({id: row.id, disabled: pending});
    const droppable = useDroppable({id: row.id, disabled: pending});
    const transform = draggable.transform;
    const style = transform
        ? {transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`}
        : undefined;
    const setNodeRef = (node) => {
        draggable.setNodeRef(node);
        droppable.setNodeRef(node);
    };

    return (
        <li
            ref={setNodeRef}
            className={`organization-position-row${draggable.isDragging ? " is-dragging" : ""}${droppable.isOver ? " is-over" : ""}`}
            style={style}
            data-testid="organization-position-item"
            data-place-id={row.id}
        >
            <span className="organization-position-number" aria-hidden="true">{index + 1}</span>
            <button
                type="button"
                className="organization-position-icon-button organization-position-drag-handle"
                aria-label={t("orgs.positions.drag", {name: row.text})}
                title={t("orgs.positions.drag", {name: row.text})}
                disabled={pending}
                {...draggable.attributes}
                {...draggable.listeners}
            >
                <GripVertical aria-hidden="true" size={18} />
            </button>
            <span className="organization-position-name">{row.text}</span>
            <span className="organization-position-id">ID {row.id}</span>
            <div className="organization-position-controls">
                <button
                    type="button"
                    className="organization-position-icon-button"
                    aria-label={t("orgs.positions.moveUp", {name: row.text})}
                    title={t("orgs.positions.moveUpTitle")}
                    disabled={pending || index === 0}
                    onClick={() => onMove(index, index - 1)}
                >
                    <ArrowUp aria-hidden="true" size={17} />
                </button>
                <button
                    type="button"
                    className="organization-position-icon-button"
                    aria-label={t("orgs.positions.moveDown", {name: row.text})}
                    title={t("orgs.positions.moveDownTitle")}
                    disabled={pending || index === count - 1}
                    onClick={() => onMove(index, index + 1)}
                >
                    <ArrowDown aria-hidden="true" size={17} />
                </button>
            </div>
        </li>
    );
};

export const OrganizationPositionsPage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const queryClient = useQueryClient();
    const role = capabilities.user?.role;
    const canOrder = PLATFORM_ROLES.has(role) && capabilities.hasAction("organization.manage");
    const {countryId, countryScope, activeCountryScopeRef} = useActiveAdminCountryScope(capabilities);
    const [cityId, setCityId] = useState(null);
    const orderingKey = ["admin-place-ordering", countryId, cityId];
    const [serverRows, setServerRows] = useState([]);
    const [draftRows, setDraftRows] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [result, setResult] = useState(null);
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor),
    );

    const propertiesQuery = useQuery({
        queryKey: ["admin-place-properties", countryScope],
        queryFn: loadPlaceProperties,
        enabled: canOrder && countryId !== null,
        retry: false,
    });
    const cityOptions = useMemo(
        () => orderingCityOptions(propertiesQuery.data),
        [propertiesQuery.data],
    );

    useEffect(() => {
        setCityId(null);
    }, [countryId]);

    useEffect(() => {
        if (cityOptions.length === 0) {
            setCityId(null);
            return;
        }
        setCityId((current) => {
            if (current != null && cityOptions.some((city) => String(city.id) === String(current))) {
                return String(current);
            }
            return String(cityOptions[0].id);
        });
    }, [cityOptions]);

    const orderingQuery = useQuery({
        queryKey: orderingKey,
        queryFn: () => loadPlaceOrdering({countryId, cityId}),
        enabled: canOrder && countryId !== null && cityId !== null,
        retry: false,
    });
    const countriesQuery = useQuery({
        queryKey: ["admin-property-countries"],
        queryFn: loadPropertyCountries,
        enabled: canOrder && countryId !== null,
        retry: false,
    });

    useEffect(() => {
        setServerRows([]);
        setDraftRows([]);
        setDirty(false);
        setResult(null);
    }, [countryId, cityId]);

    useEffect(() => {
        if (!Array.isArray(orderingQuery.data) || dirty) return;
        const rows = orderingQuery.data.map(({id, text}) => ({id, text}));
        setServerRows(rows);
        setDraftRows(rows);
    }, [countryId, cityId, dirty, orderingQuery.data]);

    const saveMutation = useMutation({
        mutationFn: (variables) => savePlaceOrdering(variables),
        onSuccess: (response, variables) => {
            const requestKey = ["admin-place-ordering", variables.countryId, variables.cityId];
            queryClient.setQueryData(requestKey, variables.places);
            void queryClient.invalidateQueries({queryKey: requestKey});
            void queryClient.invalidateQueries({queryKey: ["admin-places"]});
            if (
                activeCountryScopeRef.current !== variables.countryId
                || String(cityId) !== String(variables.cityId)
            ) return;
            setServerRows(variables.places);
            setDraftRows(variables.places);
            setDirty(false);
            setResult({
                kind: "success",
                message: response?.message || t("orgs.positions.saved"),
            });
        },
        onError: async (error, variables) => {
            const driftMessage = membershipDriftMessage(error);
            if (driftMessage) {
                const requestKey = ["admin-place-ordering", variables.countryId, variables.cityId];
                try {
                    await queryClient.fetchQuery({
                        queryKey: requestKey,
                        queryFn: () => loadPlaceOrdering({
                            countryId: variables.countryId,
                            cityId: variables.cityId,
                        }),
                    });
                } catch {
                    void queryClient.invalidateQueries({queryKey: requestKey});
                }
            }
            if (
                activeCountryScopeRef.current !== variables.countryId
                || String(cityId) !== String(variables.cityId)
            ) return;
            setResult({kind: "error", message: driftMessage || mapApiError(error).message});
        },
    });

    if (!canOrder) {
        return (
            <DataState
                title={t("orgs.positions.unavailableTitle")}
                copy={t("orgs.positions.unavailableCopy")}
            />
        );
    }

    const changeDraft = (nextRows) => {
        setDraftRows(nextRows);
        setDirty(true);
        setResult(null);
    };

    const handleMove = (fromIndex, toIndex) => {
        if (saveMutation.isPending) return;
        changeDraft(moveRow(draftRows, fromIndex, toIndex));
    };

    const handleDragEnd = ({active, over}) => {
        if (!over || saveMutation.isPending || String(active.id) === String(over.id)) return;
        const fromIndex = draftRows.findIndex((row) => String(row.id) === String(active.id));
        const toIndex = draftRows.findIndex((row) => String(row.id) === String(over.id));
        if (fromIndex < 0 || toIndex < 0) return;
        changeDraft(moveRow(draftRows, fromIndex, toIndex));
    };

    const handleReset = () => {
        const latestRows = Array.isArray(orderingQuery.data)
            ? orderingQuery.data.map(({id, text}) => ({id, text}))
            : serverRows;
        setServerRows(latestRows);
        setDraftRows(latestRows);
        setDirty(false);
        setResult(null);
    };

    const handleSave = () => {
        if (
            !dirty
            || saveMutation.isPending
            || countryId === null
            || cityId === null
            || activeCountryScopeRef.current !== countryId
        ) return;
        setResult(null);
        saveMutation.mutate({
            countryId,
            cityId,
            places: draftRows.map(({id, text}) => ({id, text})),
        });
    };

    const selectedCountry = (countriesQuery.data || []).find(
        (country) => String(country.id) === countryId,
    );
    const selectedCountryName = selectedCountry?.name
        || (String(capabilities.country?.id) === countryId ? capabilities.country?.name : null)
        || t("orgs.positions.countryFallback");
    const selectedCity = cityOptions.find((city) => String(city.id) === String(cityId));

    return (
        <div className="admin-page organization-positions-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{t("orgs.positions.title")}</h1>
                    <p className="admin-page-copy">
                        {t("orgs.positions.copy")}
                    </p>
                    {countryId ? (
                        <p className="admin-page-copy">
                            {t("orgs.positions.selectedCountry", {name: selectedCountryName, id: countryId})}
                            {selectedCity ? t("orgs.positions.selectedCity", {name: selectedCity.name, id: selectedCity.id}) : null}
                        </p>
                    ) : null}
                </div>
                <Link className="admin-button admin-button-secondary" to="/employers">
                    <ArrowLeft aria-hidden="true" size={15} /> {t("orgs.positions.back")}
                </Link>
            </header>

            {countryId === null ? (
                <DataState
                    title={t("orgs.positions.pickCountryTitle")}
                    copy={t("orgs.positions.pickCountryCopy")}
                />
            ) : propertiesQuery.isLoading ? (
                <DataState title={t("orgs.positions.loadingCities")} copy={t("orgs.positions.loadingCitiesCopy")} />
            ) : propertiesQuery.isError ? (
                <DataState title={t("orgs.positions.citiesError")} copy={mapApiError(propertiesQuery.error).message} />
            ) : cityOptions.length === 0 ? (
                <DataState title={t("orgs.positions.noCitiesTitle")} copy={t("orgs.positions.noCitiesCopy")} />
            ) : (
                <>
                    <div className="organization-position-toolbar">
                        <ToolbarSelect
                            label={t("orgs.common.city")}
                            value={cityId || ""}
                            onChange={(event) => setCityId(event.target.value || null)}
                        >
                            {cityOptions.map((city) => (
                                <option key={city.id} value={city.id}>
                                    {city.name}
                                </option>
                            ))}
                        </ToolbarSelect>
                    </div>

                    {cityId === null ? (
                        <DataState
                            title={t("orgs.positions.pickCityTitle")}
                            copy={t("orgs.positions.pickCityCopy")}
                        />
                    ) : orderingQuery.isLoading ? (
                        <DataState title={t("orgs.positions.loadingTitle")} copy={t("orgs.positions.loadingCopy")} />
                    ) : orderingQuery.isError ? (
                        <DataState title={t("orgs.positions.errorTitle")} copy={mapApiError(orderingQuery.error).message} />
                    ) : draftRows.length === 0 ? (
                        <DataState
                            title={t("orgs.positions.emptyTitle")}
                            copy={t("orgs.positions.emptyCopy")}
                        />
                    ) : (
                        <section className="organization-position-workspace" aria-busy={saveMutation.isPending}>
                            <div className="organization-position-toolbar">
                                <div>
                                    <strong>{t("orgs.positions.count", {count: draftRows.length})}</strong>
                                    {dirty ? <span role="status">{t("orgs.positions.dirty")}</span> : <span>{t("orgs.positions.synced")}</span>}
                                </div>
                                <div className="organization-position-actions">
                                    <button
                                        type="button"
                                        className="admin-button admin-button-secondary"
                                        disabled={!dirty || saveMutation.isPending}
                                        onClick={handleReset}
                                    >
                                        <RotateCcw aria-hidden="true" size={15} /> {t("orgs.positions.reset")}
                                    </button>
                                    <button
                                        type="button"
                                        className="admin-button admin-button-primary"
                                        disabled={!dirty || saveMutation.isPending}
                                        onClick={handleSave}
                                    >
                                        <Save aria-hidden="true" size={15} />
                                        {saveMutation.isPending ? t("orgs.positions.saving") : t("orgs.positions.save")}
                                    </button>
                                </div>
                            </div>

                            <MutationResultBanner result={result} />

                            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                                <ol className="organization-position-list" aria-label={t("orgs.positions.listLabel")}>
                                    {draftRows.map((row, index) => (
                                        <PositionRow
                                            key={row.id}
                                            row={row}
                                            index={index}
                                            count={draftRows.length}
                                            pending={saveMutation.isPending}
                                            onMove={handleMove}
                                        />
                                    ))}
                                </ol>
                            </DndContext>
                        </section>
                    )}
                </>
            )}
        </div>
    );
};
